/**
 * Login Page — email + password (@masholdings.com accounts).
 * Refusals come with a code (see AuthService) so the page can say what to do
 * next: create the account, wait for approval, or contact the admin.
 */
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Container, Paper, Button, Typography, CircularProgress, TextField, Alert, Link,
} from '@mui/material';
import { useAuth, takeAuthNotice } from '../contexts/AuthContext';

// Codes that are a next step rather than a mistake
const INFO_CODES = ['pending', 'no_password'];

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  // { message, code } — also set when the app signed the user out (e.g. disabled)
  const [error, setError] = useState(() => takeAuthNotice());

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login(email.trim(), password);
    } catch (err) {
      setError({ message: err.message, code: err.code });
      setLoading(false);
    }
  };

  const signupLink = `/signup?email=${encodeURIComponent(email.trim())}`;

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      background: 'linear-gradient(135deg, #424242 0%, #757575 50%, #D32F2F 100%)',
    }}>
      <Container maxWidth="sm">
        <Paper elevation={24} sx={{ p: 5, borderRadius: 2 }}>
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Typography variant="h4" component="h1" gutterBottom fontWeight={600}>
              Job Posting Automation
            </Typography>
            <Typography variant="body1" color="text.secondary">
              MAS Holdings - Automated Design Generation
            </Typography>
          </Box>

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Work email"
              type="email"
              autoComplete="email"
              placeholder="name@masholdings.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              autoFocus
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
            />

            {error && (
              <Alert
                severity={INFO_CODES.includes(error.code) ? 'info' : 'error'}
                sx={{ mt: 2 }}
                action={['no_password', 'no_account'].includes(error.code) ? (
                  <Button component={RouterLink} to={signupLink} color="inherit" size="small" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                    Create account
                  </Button>
                ) : null}
              >
                {error.message}
              </Alert>
            )}

            <Button
              fullWidth
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                mt: 3, mb: 2, py: 1.6,
                fontSize: '1.05rem', fontWeight: 600,
                background: 'linear-gradient(135deg, #D32F2F 0%, #B71C1C 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #B71C1C 0%, #7f0000 100%)' },
              }}
            >
              {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Log in'}
            </Button>
          </form>

          <Typography variant="body2" color="text.secondary" align="center">
            First time here?{' '}
            <Link component={RouterLink} to={signupLink} fontWeight={600}>
              Create an account
            </Link>{' '}
            with your <strong>@masholdings.com</strong> email. If an admin has already added you, you can
            start straight away; otherwise an admin approves your account first.
          </Typography>
        </Paper>

        <Typography variant="body2" align="center" sx={{ mt: 3, color: 'white', opacity: 0.9 }}>
          2026 MAS Holdings. All rights reserved.
        </Typography>
      </Container>
    </Box>
  );
}
